import PaginatedTable from "Components/shared/PaginatedTable";
import { useNavigate } from "react-router-dom";
import { useRTL } from "hooks/isRTL";
import CustomerSearch from "Components/CustomerManagement/CustomerSearch";
import { Switch, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCustomerRequests,
  toggleAcceptZero,
} from "services/customerManagement";
import { RegistrationStatusEnum } from "enums/shared";
import { fallbackValue } from "constants/fallbackConstant";
import { useAtom } from "jotai";
import { useState } from "react";
import CustomSnackbar from "Components/shared/CustomSnackbar";
import {
  customerFiltersAtom,
  customerPageAtom,
  customerRowsPerPageAtom,
  descendingEmailSortAtom,
  descendingNameSortAtom,
} from "./CustomerManagementTaps/atom/customerTableAtoms";

const CustomerTable = () => {
  const { t } = useRTL();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useAtom(customerFiltersAtom);
  const [page, setPage] = useAtom(customerPageAtom);
  const [rowsPerPage, setRowsPerPage] = useAtom(customerRowsPerPageAtom);
  const [descendingNameSort, setDescendingNameSort] = useAtom(descendingNameSortAtom);
  const [descendingEmailSort, setDescendingEmailSort] = useAtom(descendingEmailSortAtom);
  const [snackbarDetails, setSnackbarDetails] = useState({ message: "", severity: "" });

  // 🔹 Modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchCustomerRequests = async () => {
    return getCustomerRequests(
      rowsPerPage,
      page + 1,
      filters.fullName,
      filters.status,
      filters.phoneNumber,
      filters.email,
      descendingNameSort,
      descendingEmailSort
    );
  };

  // ✅ Mutation with backend call
  const toggleMutation = useMutation({
    mutationFn: ({ senderId, acceptZero, zeroStartDate, zeroEndDate }) =>
      toggleAcceptZero(senderId, acceptZero, zeroStartDate, zeroEndDate),
    onSuccess: () => {
      queryClient.invalidateQueries(["customerRequests"]);
      setSnackbarDetails({
        message: t("zeroFeesUpdateSuccess"),
        severity: "success",
      });
    },
    onError: () => {
      setSnackbarDetails({
        message: t("zeroFeesUpdateError"),
        severity: "error",
      });
    },
  });

  // 🔹 Open modal when enabling zero fees
  const handleToggleAcceptZero = (senderId, currentValue) => {
    if (!currentValue) {
      // Turning ON → open modal to pick dates
      setSelectedSenderId(senderId);
      setOpenModal(true);
    } else {
      // Turning OFF → call directly
      toggleMutation.mutate({
        senderId,
        acceptZero: false,
        zeroStartDate: null,
        zeroEndDate: null,
      });
    }
  };

  // 🔹 Confirm modal action
  const handleConfirm = () => {
    if (!startDate || !endDate) {
      setSnackbarDetails({ message: t("pleaseSelectDates"), severity: "warning" });
      return;
    }

    toggleMutation.mutate({
      senderId: selectedSenderId,
      acceptZero: true,
      zeroStartDate: startDate,
      zeroEndDate: endDate,
    });

    setOpenModal(false);
    setStartDate("");
    setEndDate("");
    setSelectedSenderId(null);
  };

  // 🔹 Cancel modal
  const handleCancel = () => {
    setOpenModal(false);
    setStartDate("");
    setEndDate("");
    setSelectedSenderId(null);
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "customerRequests",
      {
        page: page + 1,
        pageSize: rowsPerPage,
        ...filters,
        descendingNameSort,
        descendingEmailSort,
      },
    ],
    queryFn: fetchCustomerRequests,
    select: (data) => ({
      rows: data.page.map((customer) => ({
        ...customer,
        status: t(
          Object.keys(RegistrationStatusEnum).find(
            (key) => RegistrationStatusEnum[key] === customer.status
          ) || "Unknown"
        ),
        phoneNumber: customer.phoneNumber.replace("_", "") || fallbackValue,
      })),
      totalRecords: data.totalRecords,
    }),
    keepPreviousData: true,
  });

  const handleSortChange = (columnId, isDescending) => {
    if (columnId === "fullName") setDescendingNameSort(isDescending);
    else if (columnId === "email") setDescendingEmailSort(isDescending);
  };

  const handleSearch = (value, columnId) => {
    setFilters((prev) => ({ ...prev, [columnId]: value }));
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNameSearch = (value) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, fullName: value }));
  };

  const handleSnackbarClose = () => {
    setSnackbarDetails({ message: "", severity: "" });
  };

  // 🔹 Table Columns
  const columns = [
    { id: "fullName", label: t("fullName"), sortable: true },
    { id: "status", label: t("registrationStatus"), sortable: true },
    { id: "phoneNumber", label: t("phoneNumber"), sortable: true },
    { id: "email", label: t("email"), sortable: true },
    {
      id: "acceptZero",
      label: t("zeroFees"),
      sortable: false,
      renderCell: (row) => (
        <Switch
          checked={row.acceptZero}
          onChange={(e) => {
            e.stopPropagation();
            handleToggleAcceptZero(row.id, row.acceptZero);
          }}
          onClick={(e) => e.stopPropagation()}
          color="primary"
          disabled={toggleMutation.isLoading}
        />
      ),
    },
  ];

  const statusOptions = Object.entries(RegistrationStatusEnum).map(([label, value]) => ({
    value,
    label: t(label),
  }));

  const handleRegistrationStatusChange = (statuses) => {
    setFilters((prevFilters) => ({ ...prevFilters, status: statuses }));
  };

  return (
    <>
      <CustomerSearch
        setFilters={setFilters}
        setPage={setPage}
        setDescendingNameSort={setDescendingNameSort}
        searchValue={filters.fullName}
        onSearchChange={handleNameSearch}
      />

      <PaginatedTable
        columns={columns}
        rows={data?.rows || []}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={data?.totalRecords || 0}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        showActions={false}
        onRowClick={(row) =>
          navigate(`/customerManagement/CustomerDetails/${row.id}`)
        }
        onSortChange={handleSortChange}
        onSearch={handleSearch}
        descendingNameSort={descendingNameSort}
        descendingEmailSort={descendingEmailSort}
        filters={filters}
        onRegistrationStatusChange={handleRegistrationStatusChange}
        statusOptions={statusOptions}
      />

      {/* ✅ Modal for Start/End Date Selection */}
      <Dialog open={openModal} onClose={handleCancel}>
        <DialogTitle>{t("setZeroFeesDuration")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            type="date"
            label={t("zeroStartDate")}
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            type="date"
            label={t("zeroEndDate")}
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{t("cancel")}</Button>
          <Button onClick={handleConfirm} variant="contained">
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        message={snackbarDetails.message}
        severity={snackbarDetails.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default CustomerTable;
